import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';

const SERIAL_COMMA_LANGS = ['en-us', 'en-ca', 'en-gb'];

/**
 * Joins an array using commas, where the last 2 elements (if applicable) are joined with "and"
 * (or the specified `conjunction`).
 *
 * e.g.:
 * `["a", "b", "c"]` -> `"a, b, and c"`
 * `["a", "b"]` -> `"a and b"`
 */
export const syndeticJoin = (arr: unknown[], conjunction = undefined): string => {
  let conjunctionLocalized = ` ${conjunction || t`and`} `;

  if (arr.length < 3) {
    return arr.join(conjunctionLocalized);
  }

  // Add serial comma before conjunction if language is 'en-us', 'en-ca', 'en-gb'
  if (SERIAL_COMMA_LANGS.includes(i18n.locale)) {
    conjunctionLocalized = `,${conjunctionLocalized}`;
  }

  return `${arr.slice(0, -1).join(', ')}${conjunctionLocalized}${arr.slice(-1)}`;
};

/* eslint-disable prettier/prettier */
/**
 * Splices commas or conjunctions into array in a way akin to how syndetic Join splices them 
 * into a string.
 * The primary use case is to display JSX text elements with custom styling (bold, italic, etc.) joined
 * by standard-styling separators and conjunctions.
 * The last 2 elements (if applicable) are joined with "and" (or the specified `conjunction`).
 *
 * e.g.:
 * `[a, b, c]` -> `[a, ",", b, ", and", c]`
 * `[a, b]` -> `[a, " and ", b]`
 */
/* eslint-enable prettier/prettier */
/**
 * Groups an array of items into a record keyed by the return value of `keyFn`.
 *
 * e.g.:
 * `groupBy([{type: 'a'}, {type: 'b'}, {type: 'a'}], (item) => item.type)`
 * -> `{ a: [{type: 'a'}, {type: 'a'}], b: [{type: 'b'}] }`
 */
export const groupBy = <T, K extends PropertyKey>(
  arr: T[],
  keyFn: (item: T) => K
): Partial<Record<K, T[]>> =>
  arr.reduce<Partial<Record<K, T[]>>>((acc, item) => {
    const key = keyFn(item);
    (acc[key] ??= []).push(item);
    return acc;
  }, {});

export const syndeticInterleave = <T>(arr: T[], conjunction?: string): Array<T | string> => {
  const conjunctionLocalized = ` ${conjunction || t`and`} `;

  const conjunctionLocalizedWithComma = `,${conjunctionLocalized}`;

  const interleavedArray: Array<T | string> = [];

  for (const [index, value] of arr.entries()) {
    if (index !== 0) {
      if (index + 1 < arr.length) {
        interleavedArray.push(', ');
      } else if (arr.length === 2) {
        interleavedArray.push(conjunctionLocalized);
      } else {
        interleavedArray.push(conjunctionLocalizedWithComma);
      }
    }

    interleavedArray.push(value);
  }

  return interleavedArray;
};
